import {
  PigRunMapEntry,
  PigLocationState,
  PipelineSectionMarkerState,
} from '@/models/PigRunState';
import {
  TrackingWorksheet,
  TrackingWorksheetDistanceSource,
  TrackingWorksheetPassSource,
  TrackingWorksheetRow,
  TrackingWorksheetRowStatus,
} from '@/models/TrackingWorksheet';
import { VerifiedPassageSource } from '@/models/VerifiedPassageSource';
import { evaluateLegSpeed } from '@/services/PigRunSpeedRules';

const MILLISECONDS_PER_SECOND = 1000;
const EARTH_RADIUS_METERS = 6371000;

/**
 * Lifecycle states of a run that has ended: Done (received) and Cancelled.
 * They get no Next marker, ETAs or overdue values.
 */
export const ENDED_LIFECYCLE_STATES: readonly string[] = ['D', 'X'];

/** True for Done ('D') and Cancelled ('X') runs (EN-1495 review, R2-9). */
export function isEndedLifecycleState(
  lifecycleState: string | null | undefined
): boolean {
  return !!lifecycleState && ENDED_LIFECYCLE_STATES.includes(lifecycleState);
}

/**
 * Whether a Skipped Receive that is the run's Next (every Track marker passed)
 * shows "overdue" once its ETA has passed. A Skipped Receive cannot detect the
 * pig's arrival, so for now it keeps the live countdown (clamped at 0) and no
 * overdue value, until product decides (EN-1495 review Q3).
 */
export const SHOW_OVERDUE_FOR_SKIPPED_RECEIVE = false;

export interface TrackingWorksheetOptions {
  /** Defaults to SHOW_OVERDUE_FOR_SKIPPED_RECEIVE. */
  showOverdueForSkippedReceive?: boolean;
}

function cloneDate(date: Date): Date {
  return new Date(date.getTime());
}

function parseUtcDateTime(value: string | null | undefined): Date | null {
  if (!value) return null;

  const normalizedValue = value.trim();
  const sqlDateMatch = normalizedValue.match(
    /^(\d{4})-(\d{1,2})-(\d{1,2})[ T](\d{1,2}):(\d{2}):(\d{2})(?:\.(\d{1,7}))?Z?$/
  );
  if (sqlDateMatch) {
    const [, year, month, day, hour, minute, second, fraction = ''] =
      sqlDateMatch;
    const milliseconds = Number(fraction.slice(0, 3).padEnd(3, '0'));
    const parsed = new Date(
      Date.UTC(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second),
        milliseconds
      )
    );
    if (
      parsed.getUTCFullYear() === Number(year) &&
      parsed.getUTCMonth() === Number(month) - 1 &&
      parsed.getUTCDate() === Number(day) &&
      parsed.getUTCHours() === Number(hour) &&
      parsed.getUTCMinutes() === Number(minute) &&
      parsed.getUTCSeconds() === Number(second)
    ) {
      return parsed;
    }
    return null;
  }

  if (
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,7})?(?:Z|[+-]\d{2}:\d{2})$/i.test(
      normalizedValue
    )
  ) {
    return null;
  }

  const timestamp = Date.parse(normalizedValue);
  return Number.isNaN(timestamp) ? null : new Date(timestamp);
}

function parseEtaDurationSeconds(
  value: string | number | null | undefined
): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value >= 0 ? Math.round(value) : null;
  }
  if (!value) return null;

  const trimmedValue = value.trim();
  if (!trimmedValue) return null;

  const numericSeconds = Number(trimmedValue);
  if (Number.isFinite(numericSeconds) && numericSeconds >= 0) {
    return Math.round(numericSeconds);
  }

  const timeSpanMatch = trimmedValue.match(
    /^(?:(\d+)\.)?(\d{1,2}):([0-5]\d):([0-5]\d)(?:\.\d{1,7})?$/
  );
  if (!timeSpanMatch) return null;

  const [, days = '0', hours, minutes, seconds] = timeSpanMatch;
  const parsedHours = Number(hours);
  if (parsedHours > 23) return null;

  return (
    Number(days) * 86400 +
    parsedHours * 3600 +
    Number(minutes) * 60 +
    Number(seconds)
  );
}

function resolveEtaUtc(
  value: string | number | null | undefined,
  serverTimeUtc: Date | null
): Date | null {
  const durationSeconds = parseEtaDurationSeconds(value);
  if (durationSeconds !== null && serverTimeUtc) {
    return new Date(
      serverTimeUtc.getTime() + durationSeconds * MILLISECONDS_PER_SECOND
    );
  }

  return typeof value === 'string' ? parseUtcDateTime(value) : null;
}

function uniqueLinkedUids(marker: PipelineSectionMarkerState): number[] {
  return [
    ...new Set((marker.manualLinkDevices || []).map((device) => device.uid)),
  ]
    .filter((uid) => Number.isFinite(uid) && uid > 0)
    .sort((left, right) => left - right);
}

function isNonNegativeFiniteNumber(value: number | undefined): value is number {
  return value !== undefined && Number.isFinite(value) && value >= 0;
}

function hasValidCoordinates(marker: PipelineSectionMarkerState): boolean {
  return (
    Number.isFinite(marker.latitude) &&
    Number.isFinite(marker.longitude) &&
    marker.latitude >= -90 &&
    marker.latitude <= 90 &&
    marker.longitude >= -180 &&
    marker.longitude <= 180
  );
}

function haversineDistanceMeters(
  from: PipelineSectionMarkerState,
  to: PipelineSectionMarkerState
): number | null {
  if (!hasValidCoordinates(from) || !hasValidCoordinates(to)) return null;

  const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
  const latitudeDelta = toRadians(to.latitude - from.latitude);
  const longitudeDelta = toRadians(to.longitude - from.longitude);
  const a =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(toRadians(from.latitude)) *
      Math.cos(toRadians(to.latitude)) *
      Math.sin(longitudeDelta / 2) ** 2;

  return EARTH_RADIUS_METERS * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function measuredPipelineDistance(
  marker: PipelineSectionMarkerState
): number | null {
  const value = marker.pipelineDistance;
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? value
    : null;
}

function hasValidPipelineStationing(
  markers: PipelineSectionMarkerState[]
): boolean {
  if (markers.length === 0) return false;

  let previousDistance: number | null = null;
  for (let index = 0; index < markers.length; index += 1) {
    const pipelineDistance = measuredPipelineDistance(markers[index]);
    if (pipelineDistance === null) return false;

    if (index === 0) {
      if (pipelineDistance !== 0) return false;
    } else if (
      previousDistance === null ||
      pipelineDistance <= previousDistance
    ) {
      return false;
    }

    previousDistance = pipelineDistance;
  }

  return true;
}

function measuredLegDistanceMeters(
  from: PipelineSectionMarkerState,
  to: PipelineSectionMarkerState
): number | null {
  const fromDistance = measuredPipelineDistance(from);
  const toDistance = measuredPipelineDistance(to);
  return fromDistance === null || toDistance === null
    ? null
    : toDistance - fromDistance;
}

function selectedSourcesByMarkerId(
  sources: VerifiedPassageSource[]
): Map<number, VerifiedPassageSource> {
  const selectedSources = new Map<number, VerifiedPassageSource>();
  for (const source of sources) {
    if (!selectedSources.has(source.pipelineSectionMarkerId)) {
      selectedSources.set(source.pipelineSectionMarkerId, source);
    }
  }
  return selectedSources;
}

function isTrackMarker(marker: PipelineSectionMarkerState): boolean {
  return marker.trackState === 'T';
}

function buildPassSource(
  source: VerifiedPassageSource | undefined
): TrackingWorksheetPassSource | null {
  if (!source) return null;
  if (source.isManual) return { kind: 'manual', uid: null };
  if (source.uid !== null && source.uid > 0) {
    return { kind: 'agm', uid: source.uid };
  }
  return null;
}

/**
 * Index of the next Track marker the pig has not passed (after the farthest
 * passed Track marker), or -1 when every Track marker ahead is passed. The
 * markers must be sorted by sortOrder.
 */
function findNextTrackMarkerIndex(
  markers: PipelineSectionMarkerState[]
): number {
  let lastPassedIndex = -1;
  for (let index = 0; index < markers.length; index += 1) {
    if (isTrackMarker(markers[index]) && markers[index].hasVerifiedPassage) {
      lastPassedIndex = index;
    }
  }

  for (let index = lastPassedIndex + 1; index < markers.length; index += 1) {
    if (isTrackMarker(markers[index]) && !markers[index].hasVerifiedPassage) {
      return index;
    }
  }

  return -1;
}

function getRowStatus(
  marker: PipelineSectionMarkerState,
  index: number,
  nextTrackMarkerIndex: number,
  isCompletedRun: boolean
): TrackingWorksheetRowStatus {
  if (!isTrackMarker(marker)) return 'skipped';
  if (marker.hasVerifiedPassage) return 'passed';
  if (isCompletedRun) return 'not-verified';
  if (index === nextTrackMarkerIndex) return 'next';
  return 'pending';
}

function buildRows(
  markers: PipelineSectionMarkerState[],
  sourcesByMarkerId: Map<number, VerifiedPassageSource>,
  isCompletedRun: boolean,
  distanceFromLaunchSource: TrackingWorksheetDistanceSource
): TrackingWorksheetRow[] {
  const nextTrackMarkerIndex = isCompletedRun
    ? -1
    : findNextTrackMarkerIndex(markers);
  let cumulativeGpsDistanceMeters: number | null = 0;

  return markers.map((marker, index) => {
    const source = marker.hasVerifiedPassage
      ? sourcesByMarkerId.get(marker.pipelineSectionMarkerId)
      : undefined;
    const previousMarker = index > 0 ? markers[index - 1] : undefined;
    const gpsLegDistanceMeters = previousMarker
      ? haversineDistanceMeters(previousMarker, marker)
      : null;
    const measuredLegMeters = previousMarker
      ? measuredLegDistanceMeters(previousMarker, marker)
      : null;
    const legDistanceMeters =
      distanceFromLaunchSource === 'measured'
        ? measuredLegMeters
        : gpsLegDistanceMeters;

    if (distanceFromLaunchSource === 'gps' && index > 0) {
      cumulativeGpsDistanceMeters =
        cumulativeGpsDistanceMeters === null || gpsLegDistanceMeters === null
          ? null
          : cumulativeGpsDistanceMeters + gpsLegDistanceMeters;
    }

    const distanceFromLaunchMeters =
      distanceFromLaunchSource === 'measured'
        ? measuredPipelineDistance(marker)
        : cumulativeGpsDistanceMeters;
    const hasValidLocation = hasValidCoordinates(marker);

    return {
      pipelineSectionMarkerId: marker.pipelineSectionMarkerId,
      sortOrder: marker.sortOrder,
      markerName: marker.name,
      description: marker.description,
      trackState: marker.trackState,
      status: getRowStatus(marker, index, nextTrackMarkerIndex, isCompletedRun),
      linkedUids: uniqueLinkedUids(marker),
      passSource: buildPassSource(source),
      passageTimeUtc: source ? parseUtcDateTime(source.passageTimeStamp) : null,
      etaUtc: null,
      countdownSeconds: null,
      isSummaryNext: false,
      expectedArrivalUtc: null,
      overdueSeconds: null,
      legDistanceMeters,
      legDistanceSource:
        legDistanceMeters === null ? null : distanceFromLaunchSource,
      distanceFromLaunchMeters,
      legSpeedMetersPerSecond: null,
      legSpeedRejection: null,
      runAverageSpeedMetersPerSecond: null,
      latitude: hasValidLocation ? marker.latitude : null,
      longitude: hasValidLocation ? marker.longitude : null,
    };
  });
}

function setEta(row: TrackingWorksheetRow, etaUtc: Date, nowUtc: Date): void {
  row.etaUtc = cloneDate(etaUtc);
  row.countdownSeconds = Math.max(
    0,
    Math.ceil((etaUtc.getTime() - nowUtc.getTime()) / MILLISECONDS_PER_SECOND)
  );
}

function applyEta(row: TrackingWorksheetRow, etaUtc: Date, nowUtc: Date): void {
  if (row.status !== 'next' && row.status !== 'pending') return;
  setEta(row, etaUtc, nowUtc);
}

/**
 * The Receive row (always the last marker) while the pig has not been
 * received there, whatever its Track/Skip state. A Skipped Receive is still
 * where the run ends, so it carries the run-complete ETA (EN-1495).
 */
function upcomingReceiveRow(
  rows: TrackingWorksheetRow[]
): TrackingWorksheetRow | null {
  if (rows.length < 2) return null;
  const receiveRow = rows[rows.length - 1];
  return receiveRow.status === 'passed' || receiveRow.status === 'not-verified'
    ? null
    : receiveRow;
}

function applyLiveEtas(
  rows: TrackingWorksheetRow[],
  pigLocation: PigLocationState | null,
  nowUtc: Date
): Date | null {
  const serverTimeUtc = parseUtcDateTime(pigLocation?.serverTime);
  const nextTrackEta = pigLocation?.etaToNextTrack;
  const nextEtaUtc = resolveEtaUtc(
    nextTrackEta === undefined || nextTrackEta === ''
      ? pigLocation?.etaToNext
      : nextTrackEta,
    serverTimeUtc
  );
  const endEtaUtc = resolveEtaUtc(pigLocation?.etaToEnd, serverTimeUtc);
  const nextIndex = rows.findIndex((row) => row.status === 'next');
  if (nextIndex >= 0 && nextEtaUtc) {
    applyEta(rows[nextIndex], nextEtaUtc, nowUtc);

    const speed = pigLocation?.speed;
    if (Number.isFinite(speed) && speed !== undefined && speed > 0) {
      let projectedEtaUtc = nextEtaUtc;
      for (let index = nextIndex + 1; index < rows.length; index += 1) {
        const row = rows[index];
        if (row.legDistanceMeters === null) break;

        projectedEtaUtc = new Date(
          projectedEtaUtc.getTime() +
            (row.legDistanceMeters / speed) * MILLISECONDS_PER_SECOND
        );
        if (row.status === 'next' || row.status === 'pending') {
          applyEta(row, projectedEtaUtc, nowUtc);
        }
      }
      rows[nextIndex].legSpeedMetersPerSecond = speed;
    }
  }

  // EN-1495: the run-complete ETA (etaToEnd) belongs to the Receive row only.
  // It used to overwrite the last upcoming Track row, so with a Skipped
  // Receive the next Track marker showed the Receive ETA.
  const receiveRow = upcomingReceiveRow(rows);
  if (receiveRow && endEtaUtc) {
    setEta(receiveRow, endEtaUtc, nowUtc);
  }

  return endEtaUtc;
}

/**
 * Leg speeds between passed rows (and from the launch to the first one). A leg
 * the backend ignores for the current speed (PigRunSpeedRules, R2-7) keeps
 * its raw speed for display and gets legSpeedRejection.
 */
function applyPassageSpeeds(
  rows: TrackingWorksheetRow[],
  launchTimeUtc: Date | null,
  expectedSpeedMetersPerSecond: number | null | undefined
): number | null {
  let previousPassedRow: TrackingWorksheetRow | null = null;
  let distanceSincePreviousPassMeters: number | null = 0;
  let lastPassedRow: TrackingWorksheetRow | null = null;
  let hasPassedMarker = false;

  for (const row of rows) {
    if (previousPassedRow) {
      distanceSincePreviousPassMeters =
        distanceSincePreviousPassMeters === null ||
        row.legDistanceMeters === null
          ? null
          : distanceSincePreviousPassMeters + row.legDistanceMeters;
    }

    if (row.status !== 'passed') continue;
    if (!row.passageTimeUtc) {
      previousPassedRow = null;
      distanceSincePreviousPassMeters = null;
      hasPassedMarker = true;
      continue;
    }

    if (previousPassedRow && distanceSincePreviousPassMeters !== null) {
      const durationSeconds =
        (row.passageTimeUtc.getTime() -
          previousPassedRow.passageTimeUtc!.getTime()) /
        MILLISECONDS_PER_SECOND;
      const evaluation = evaluateLegSpeed(
        distanceSincePreviousPassMeters,
        durationSeconds,
        expectedSpeedMetersPerSecond
      );
      row.legSpeedMetersPerSecond = evaluation.speedMetersPerSecond;
      row.legSpeedRejection = evaluation.rejection;
    } else if (
      !hasPassedMarker &&
      launchTimeUtc &&
      row.distanceFromLaunchMeters !== null
    ) {
      const durationSeconds =
        (row.passageTimeUtc.getTime() - launchTimeUtc.getTime()) /
        MILLISECONDS_PER_SECOND;
      const evaluation = evaluateLegSpeed(
        row.distanceFromLaunchMeters,
        durationSeconds,
        expectedSpeedMetersPerSecond
      );
      row.legSpeedMetersPerSecond = evaluation.speedMetersPerSecond;
      row.legSpeedRejection = evaluation.rejection;
    }

    previousPassedRow = row;
    distanceSincePreviousPassMeters = 0;
    lastPassedRow = row;
    hasPassedMarker = true;
  }

  if (
    !launchTimeUtc ||
    !lastPassedRow?.passageTimeUtc ||
    lastPassedRow.distanceFromLaunchMeters === null ||
    lastPassedRow.distanceFromLaunchMeters <= 0
  ) {
    return null;
  }

  const durationSeconds =
    (lastPassedRow.passageTimeUtc.getTime() - launchTimeUtc.getTime()) /
    MILLISECONDS_PER_SECOND;
  return durationSeconds > 0
    ? lastPassedRow.distanceFromLaunchMeters / durationSeconds
    : null;
}

/**
 * When the pig should have reached rows[targetIndex], from verified data only:
 * the passage time of the last passed row before it (or the launch time when
 * none is passed) plus the leg distances from there at the current speed. The
 * speed is the one PigRunManager sends; it is not changed here. Null when a
 * passage time, launch time, leg distance or usable speed is missing.
 */
function expectedArrivalFromVerifiedData(
  rows: TrackingWorksheetRow[],
  targetIndex: number,
  launchTimeUtc: Date | null,
  speedMetersPerSecond: number | undefined
): Date | null {
  if (
    speedMetersPerSecond === undefined ||
    !Number.isFinite(speedMetersPerSecond) ||
    speedMetersPerSecond <= 0
  ) {
    return null;
  }

  let anchorIndex = -1;
  for (let index = targetIndex - 1; index >= 0; index -= 1) {
    if (rows[index].status === 'passed') {
      anchorIndex = index;
      break;
    }
  }
  const anchorTimeUtc =
    anchorIndex >= 0 ? rows[anchorIndex].passageTimeUtc : launchTimeUtc;
  if (!anchorTimeUtc) return null;

  let distanceMeters = 0;
  for (
    let index = Math.max(anchorIndex, 0) + 1;
    index <= targetIndex;
    index += 1
  ) {
    const legDistanceMeters = rows[index].legDistanceMeters;
    if (legDistanceMeters === null) return null;
    distanceMeters += legDistanceMeters;
  }

  return new Date(
    anchorTimeUtc.getTime() +
      (distanceMeters / speedMetersPerSecond) * MILLISECONDS_PER_SECOND
  );
}

function applyOverdue(
  rows: TrackingWorksheetRow[],
  nextRow: TrackingWorksheetRow,
  launchTimeUtc: Date | null,
  speedMetersPerSecond: number | undefined,
  nowUtc: Date
): void {
  const expectedArrivalUtc = expectedArrivalFromVerifiedData(
    rows,
    rows.indexOf(nextRow),
    launchTimeUtc,
    speedMetersPerSecond
  );
  if (!expectedArrivalUtc) return;

  nextRow.expectedArrivalUtc = expectedArrivalUtc;
  nextRow.overdueSeconds = Math.max(
    0,
    Math.floor(
      (nowUtc.getTime() - expectedArrivalUtc.getTime()) /
        MILLISECONDS_PER_SECOND
    )
  );
}

/**
 * Builds the worksheet view model from canonical active-run state. The caller
 * supplies the clock so countdown calculations stay deterministic and testable.
 */
export function buildTrackingWorksheet(
  entry: PigRunMapEntry,
  now: Date,
  options: TrackingWorksheetOptions = {}
): TrackingWorksheet {
  if (Number.isNaN(now.getTime())) {
    throw new Error('Tracking worksheet requires a valid clock value.');
  }

  const builtAtUtc = cloneDate(now);
  const markers = [...entry.markers].sort(
    (left, right) => left.sortOrder - right.sortOrder
  );
  // A cancelled run ('X') is treated like a completed one (EN-1495 review).
  const isCompletedRun = isEndedLifecycleState(entry.pigRun.lifecycleState);
  const sourcesByMarkerId = selectedSourcesByMarkerId(
    entry.verifiedPassageSources || []
  );
  const launchTimeUtc = parseUtcDateTime(entry.pigRun.launchDate);
  const distanceFromLaunchSource = hasValidPipelineStationing(markers)
    ? 'measured'
    : 'gps';
  const rows = buildRows(
    markers,
    sourcesByMarkerId,
    isCompletedRun,
    distanceFromLaunchSource
  );
  const endEtaUtc = isCompletedRun
    ? null
    : applyLiveEtas(rows, entry.pigLocation, builtAtUtc);
  const runAverageSpeedMetersPerSecond = applyPassageSpeeds(
    rows,
    launchTimeUtc,
    entry.pigRun.expectedSpeed
  );
  for (const row of rows) {
    row.runAverageSpeedMetersPerSecond = runAverageSpeedMetersPerSecond;
  }

  // EN-1495: once every Track marker is passed, the next marker is the Receive
  // (even when Skipped) until the run is received. The backend targets it too
  // (PigRunAlgorithmHelper.FindEndingLineSegmentMarkerWithDistanceIndex), and
  // the row is flagged isSummaryNext so the table agrees with the summary.
  const nextRow =
    rows.find((row) => row.status === 'next') ??
    (isCompletedRun ? null : upcomingReceiveRow(rows));
  const currentSpeed = entry.pigLocation?.speed;
  if (nextRow) {
    nextRow.isSummaryNext = true;
    const showOverdueForSkippedReceive =
      options.showOverdueForSkippedReceive ?? SHOW_OVERDUE_FOR_SKIPPED_RECEIVE;
    if (
      entry.pigLocation &&
      (nextRow.status === 'next' || showOverdueForSkippedReceive)
    ) {
      applyOverdue(rows, nextRow, launchTimeUtc, currentSpeed, builtAtUtc);
    }
  }
  const serverTimeUtc = parseUtcDateTime(entry.pigLocation?.serverTime);
  return {
    summary: {
      pigRunId: entry.pigRun.pigRunId,
      runName: entry.pigRun.name,
      jobNumber: entry.pigRun.jobNumber?.trim() || null,
      pipelineName: null,
      lifecycleState: entry.pigRun.lifecycleState,
      launchTimeUtc,
      receiveTimeUtc: null,
      currentSpeedMetersPerSecond: isNonNegativeFiniteNumber(currentSpeed)
        ? currentSpeed
        : null,
      verifiedMarkerCount: markers.filter((marker) => marker.hasVerifiedPassage)
        .length,
      markerCount: markers.length,
      deviceCount: entry.pigRun.devices?.length ?? null,
      passageCount: null,
      verifiedPassageCount: null,
      nextMarkerName: nextRow?.markerName ?? null,
      nextMarkerEtaUtc: nextRow?.etaUtc ?? null,
      nextMarkerOverdueSeconds: nextRow?.overdueSeconds ?? null,
      endEtaUtc,
      lastUpdatedUtc: serverTimeUtc,
    },
    distanceFromLaunchSource,
    rows,
    builtAtUtc,
  };
}

/**
 * True while at least one Track marker ahead of the pig is not passed. When it
 * is false only the Receive remains, and "time to next Track marker" has no
 * Track marker to refer to (EN-1495).
 */
export function hasUpcomingTrackMarker(entry: PigRunMapEntry): boolean {
  const markers = [...(entry.markers || [])].sort(
    (left, right) => left.sortOrder - right.sortOrder
  );
  return findNextTrackMarkerIndex(markers) >= 0;
}

function formatDuration(totalSeconds: number): string {
  const pad = (value: number) => value.toString().padStart(2, '0');
  return `${pad(Math.floor(totalSeconds / 3600))}:${pad(
    Math.floor((totalSeconds % 3600) / 60)
  )}:${pad(totalSeconds % 60)}`;
}

function formatUtcTime(date: Date): string {
  const pad = (value: number) => value.toString().padStart(2, '0');
  return `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(
    date.getUTCSeconds()
  )} UTC`;
}

/**
 * The clock-independent part of a marker popup ETA, taken from the worksheet
 * once (EN-1495 review round 2, R2-8): the popup re-formats it every second
 * with formatMarkerEta instead of rebuilding the worksheet.
 */
export interface MarkerEtaInfo {
  state: 'none' | 'passed' | 'eta';
  /** The marker's ETA (UTC), or null when there is no estimate. */
  etaUtc: Date | null;
  /**
   * When the pig should have reached the marker from verified data (set for
   * the summary Next marker only), used for "Overdue by".
   */
  expectedArrivalUtc: Date | null;
}

/**
 * Builds the worksheet at `now` and returns the selected marker's ETA data.
 * Rebuild it when the run data changes, not every second.
 */
export function resolveMarkerEta(
  entry: PigRunMapEntry,
  pipelineSectionMarkerId: number,
  now: Date,
  options: TrackingWorksheetOptions = {}
): MarkerEtaInfo {
  const row = buildTrackingWorksheet(entry, now, options).rows.find(
    (candidate) => candidate.pipelineSectionMarkerId === pipelineSectionMarkerId
  );
  if (!row) return { state: 'none', etaUtc: null, expectedArrivalUtc: null };
  if (row.status === 'passed') {
    return { state: 'passed', etaUtc: null, expectedArrivalUtc: null };
  }
  return {
    state: 'eta',
    etaUtc: row.etaUtc,
    // Only the summary Next row carries it (and only when overdue applies).
    expectedArrivalUtc: row.expectedArrivalUtc,
  };
}

/**
 * Formats MarkerEtaInfo at `now`: "Passed", "Overdue by hh:mm:ss (due HH:MM:SS
 * UTC)" once now is past the expected arrival, "hh:mm:ss (HH:MM:SS UTC)"
 * counting down to the ETA (clamped at 0), or "—" when there is no estimate.
 * Cheap enough to run every second.
 */
export function formatMarkerEta(info: MarkerEtaInfo, now: Date): string {
  if (info.state === 'passed') return 'Passed';
  if (info.state === 'none') return '—';
  if (info.expectedArrivalUtc) {
    // Same rounding as applyOverdue (floor) and setEta (ceil).
    const overdueSeconds = Math.floor(
      (now.getTime() - info.expectedArrivalUtc.getTime()) /
        MILLISECONDS_PER_SECOND
    );
    if (overdueSeconds > 0) {
      return `Overdue by ${formatDuration(overdueSeconds)} (due ${formatUtcTime(
        info.expectedArrivalUtc
      )})`;
    }
  }
  if (!info.etaUtc) return '—';

  const countdownSeconds = Math.max(
    0,
    Math.ceil((info.etaUtc.getTime() - now.getTime()) / MILLISECONDS_PER_SECOND)
  );
  return `${formatDuration(countdownSeconds)} (${formatUtcTime(info.etaUtc)})`;
}

/**
 * Text for the marker popup "ETA to marker" line: the selected marker's own
 * ETA from the worksheet, "Overdue by hh:mm:ss" once the pig is past its
 * expected arrival there without a verified passage, "Passed" once it has a
 * verified passage, or "—" when there is no estimate. Replaces the SignalR
 * etaToNext value, which PigRunManager sends as a constant placeholder (5.0)
 * (EN-1495). Same as formatMarkerEta(resolveMarkerEta(...), now).
 */
export function describeMarkerEta(
  entry: PigRunMapEntry,
  pipelineSectionMarkerId: number,
  now: Date,
  options: TrackingWorksheetOptions = {}
): string {
  return formatMarkerEta(
    resolveMarkerEta(entry, pipelineSectionMarkerId, now, options),
    now
  );
}

export {
  findNextTrackMarkerIndex,
  parseEtaDurationSeconds,
  parseUtcDateTime,
  resolveEtaUtc,
};
